import React, { useEffect, useState } from "react";
import "./PortfolioModal.scss";
import { Form, Modal, DatePicker, Skeleton, Button, Input, Divider } from "antd";
import CustomSelect from "../../../Reuseable/Select/Select";
import InputField from "../../../Reuseable/InputField/InputField";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import DragUpload from "../../../Reuseable/DragUpload/DragUpload";
import logo from "../../../assets/Icons/fileLogo.svg";
import download from "../../../assets/Icons/downloadIconPortfolio.svg";
import trash from "../../../assets/Icons/dustbinPortfolio.svg";
import completed from "../../../assets/Icons/completed.svg";
import dayjs from "dayjs";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { fetchPortfolioFiles } from "../../../store/portfolioSlice";
import { ReactComponent as Down } from "../../../assets/Icons/DownArrow.svg";
import { DeleteTwoTone } from "@ant-design/icons";
import {
  deletePortfolioFile,
  downloadAllFiles,
} from "../../../assets/api/portfolio";
import toast from "react-hot-toast";

const PortfolioModal = ({
  isOpen,
  onClose,
  userData = {},
  onSave,
  mode = "add",
  setUserData,
  setFile,
  file,
  load,
  keepFormValues = false,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const { uploaded_files, uploaded_files_loading } = useSelector(
    (state) => state.portfolio,
  );
  const [companies, setCompanies] = useState([]);
  const [newCompany, setNewCompany] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [addingCompany, setAddingCompany] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await axios.get("portfolio/companies/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = response.data?.data;
        if (data && Array.isArray(data?.companies)) {
          setCompanies(data?.companies);
        }
      } catch (error) {
        console.error("Failed to fetch companies in modal", error);
      }
    };
    if (isOpen && token) {
      fetchCompanies();
    }
  }, [isOpen, token]);

  const handleAddCompany = async (e) => {
    if (e) {
      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
    }
    if (!newCompany.trim()) {
      setCompanyError("Company name is required");
      return;
    }
    setCompanyError("");
    setAddingCompany(true);
    try {
      const response = await axios.post(
        "portfolio/companies/",
        { name: newCompany },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200 || response.status === 201) {
        toast.success("Company added successfully");
        setNewCompany("");
        
        // Auto-select the newly added company
        const newCompanyId = response.data?.data?.company?.id || response.data?.data?.id;
        if (newCompanyId) {
          setCompanies((prev) => {
            if (prev.some(c => c.id === newCompanyId)) return prev;
            return [...prev, { id: newCompanyId, name: newCompany }];
          });
          form.setFieldsValue({ company_id: newCompanyId });
        }
        
        setIsCompanyModalOpen(false);
      } else {
        toast.error("Failed to add company");
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to add company");
    } finally {
      setAddingCompany(false);
    }
  };

  useEffect(() => {
    if (mode === "edit" && userData) {
      // Acquired On removed

      form.setFieldsValue({
        name: userData.name,
        company_id: userData.company_id || userData.company,
        totalCases: userData.totalCases !== undefined ? userData.totalCases : userData.total_cases,
        totalPOS: userData.totalPOS !== undefined ? userData.totalPOS : userData.total_pos,
        total_tos: userData.total_tos,
      });
    } else if (!isOpen && !keepFormValues) {
      form.resetFields();
    }
  }, [mode, userData, form, isOpen, keepFormValues]);

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        onSave?.(values);
        // onClose();
      })
      .catch((info) => {
        console.error("Validation Failed:", info);
      });
  };

  useEffect(() => {
    if (mode === "edit" && userData?.id && !userData?.file) {
      dispatch(fetchPortfolioFiles({ portfolio_id: userData?.id }));
    }
  }, [dispatch, userData]);

  const handleDeleteFile = async (id) => {
    try {
      setLoading(id);
      const res = await deletePortfolioFile(
        {
          body: {
            file_id: id,
          },
          portfolio_id: userData?.id,
        },
        token,
      );

      if (res?.status === 200) {
        toast.success(res?.data?.message);
        setLoading(null);
        dispatch(fetchPortfolioFiles({ portfolio_id: userData?.id }));
      }
    } catch (error) {
      setLoading(null);
      console.log(error, "error");
    }
  };

  const handleDownloadFile = (file_url) => {
    const link = document.createElement("a");
    link.href = file_url;
    link.setAttribute("download", "file");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadFileAll = async () => {
    try {
      setLoadingFile(true);
      const res = await downloadAllFiles(token, userData?.id);
      if (res?.status === 200) {
        let url = res?.data?.data?.download_url;
        handleDownloadFile(url);
        setLoadingFile(false);
      }
    } catch (error) {
      setLoadingFile(false);
    }
  };

  return (
    <Modal
      className="modalMainContainer"
      title={
        isCompanyModalOpen
          ? "Create New Company"
          : mode === "add"
            ? "Add new portfolio"
            : "Edit portfolio"
      }
      open={isOpen}
      onCancel={() => {
        onClose();
        setFile(null);
      }}
      footer={null}
      centered
    >
      {/* Two steps on a track. The company form slides in from the right as
          the portfolio form slides out to the left; both panes stay mounted so
          the exit animates too and the form keeps its values. */}
      <div
        className={`stepViewport ${isCompanyModalOpen ? "is-company" : ""}`}
      >
        <div className="stepTrack">
          <div className="stepPane" aria-hidden={isCompanyModalOpen}>
        <div className="portfolioStep">
          <div className="mainWrapperForModal">
            <Form form={form} layout="vertical">
              <div className="selectorWrapper">
                <div className="wrappedContainer">
                  <Form.Item
                    name="name"
                    className="formItem nameInputSect"
                    rules={[{ required: true, message: "Please enter name" }]}
                  >
                    <InputField label="Name" placeholder="Enter name" />
                  </Form.Item>
                  <Form.Item
                    name="company_id"
                    label="Company"
                    className="formItem"
                    rules={[{ required: true, message: "Please select company" }]}
                  >
                    <CustomSelect
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      open={isSelectOpen}
                      onDropdownVisibleChange={(open) => setIsSelectOpen(open)}
                      options={companies.map(c => ({ value: c.id, label: c.name }))}
                      placeholder="Select company"
                      allowClear
                      dropdownRender={(menu) => (
                        <>
                          <div
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              color: "#1890ff",
                              fontWeight: "500",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsSelectOpen(false);
                              setIsCompanyModalOpen(true);
                            }}
                          >
                            + Add Company
                          </div>
                          <Divider style={{ margin: 0 }} />
                          {menu}
                        </>
                      )}
                    />
                  </Form.Item>
                </div>

                <div className="wrappedContainer">
                  <Form.Item
                    name="totalCases"
                    className="formItem"
                    rules={[
                      { required: true, message: "Please enter total cases" },
                    ]}
                  >
                    <InputField
                      label="Total Cases"
                      placeholder="Enter total cases"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="selectorWrapper">
                <Form.Item
                  name="totalPOS"
                  className="formItem"
                  rules={[{ required: true, message: "Please enter total POS" }]}
                >
                  <InputField label="Total POS" placeholder="₹0.00 Cr" />
                </Form.Item>
                <Form.Item
                  name="total_tos"
                  className="formItem"
                  rules={[{ required: true, message: "Please enter Total TOS" }]}
                >
                  <InputField label="Total TOS" placeholder="₹0.00 Cr" />
                </Form.Item>
              </div>

              {/* Only render uploaded files if in edit mode */}
              {mode === "edit" && (
                <div className="uploadedFilesWrapper">
                  {uploaded_files?.total_files > 0 && (
                    <div className="uploadedFilesHeader">
                      <span className="sectionTitle">Uploaded File</span>
                      <button
                        className="downloadAllBtn"
                        onClick={handleDownloadFileAll}
                      >
                        {loadingFile ? "Downloading..." : "Download All"}
                      </button>
                    </div>
                  )}
                  {(uploaded_files?.total_files || uploaded_files_loading) > 0 && (
                    <div
                      className="cardWrapper"
                      style={{ maxHeight: "400px", overflow: "auto" }}
                    >
                      {(uploaded_files_loading
                        ? Array(4).fill(0)
                        : uploaded_files?.files
                      )?.map((item, index) => (
                        <div className="uploadedFileRow">
                          <div className="fileInfo">
                            <div className="fileLogo">
                              <img src={logo} alt="" />
                              {/* <img src={logo} alt="" /> */}
                            </div>
                            {uploaded_files_loading ? (
                              <Skeleton.Input
                                size="small"
                                active
                                style={{ width: "100%" }}
                              />
                            ) : (
                              <div className="fileName">
                                <span className="titleOfFile">
                                  {/* AxisBank_LoanPortfolio_June2025.xlsx */}
                                  {item?.file_name}
                                </span>
                                <span className="sizeOfFile">
                                  {item?.uploaded_on}
                                </span>
                                {/* 94KB of 94 KB. */}
                              </div>
                            )}
                          </div>
                          <div className="fileActions">
                            <Button
                              className="downloadBtn"
                              onClick={() => handleDownloadFile(item?.file_url)}
                              icon={<img src={download} alt="" />}
                            />
                            <Button
                              loading={loading === item?.id}
                              className="deleteBtn"
                              onClick={() => handleDeleteFile(item?.id)}
                              icon={<img src={trash} alt="" />}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="uploadedFilesGridSection">
                    <div className="filesGrid">
                      {userData?.uploadedFiles?.map((file, index) => (
                        <div key={index} className="fileCard">
                          <div className="fileLeft">
                            <img
                              src="/assets/icons/xlsx-icon.svg"
                              alt="file"
                              className="fileIcon"
                            />
                            <div className="fileInfo">
                              <span className="fileName">{file.name}</span>
                              <span className="fileSize">94 KB</span>
                            </div>
                          </div>
                          <div className="fileActions">
                            <button className="iconBtn downloadBtn">⬇️</button>
                            <button className="iconBtn deleteBtn">🗑️</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {file !== null ? (
                <DragUpload
                  label={"Upload more"}
                  setFile={setFile}
                  readOnly={true}
                  file={file?.[0]?.originFileObj}
                />
              ) : (
                <DragUpload
                  label={"Upload more"}
                  onChange={(file) => setFile?.(file)}
                />
              )}
              <p>Limit 200MB per file | Only XLSX, CSV file</p>
              <div className="submitBtn">
                <NormalButton type="primary" onClick={handleSave} loading={load}>
                  {mode === "add" ? "Next" : "Submit"}
                </NormalButton>
              </div>
            </Form>
          </div>
        </div>
          </div>

          <div className="stepPane" aria-hidden={!isCompanyModalOpen}>
        <div className="companyStep">
          <p className="companyStep__label">Company name</p>

          <Input
            className="companyStep__input"
            placeholder="Enter new company name..."
            value={newCompany}
            status={companyError ? "error" : ""}
            autoFocus
            onChange={(e) => {
              setNewCompany(e.target.value);
              if (companyError) setCompanyError("");
            }}
            onPressEnter={handleAddCompany}
          />

          {companyError && (
            <div className="companyStep__error">{companyError}</div>
          )}

          <div className="companyStep__foot">
            <Button
              type="text"
              className="companyStep__cancel"
              onClick={() => {
                setIsCompanyModalOpen(false);
                setNewCompany("");
                setCompanyError("");
              }}
            >
              Cancel
            </Button>
            <NormalButton
              type="primary"
              onClick={handleAddCompany}
              loading={addingCompany}
            >
              Create
            </NormalButton>
          </div>
        </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PortfolioModal;